import { isSetEvents, SetEvents } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ClientEvents';
import { pingWithFolders } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ClientEvents/ClientEventsService';
import { ServiceFetcher } from '@decisions/server/ServiceFetcher';
import { getVisibleFolders } from 'components/Router/RouterUtils';
import { eventsHub, getPubSub } from './EventsHub';

/** Helpers to DRY up things like getting the ClientEventSessionId, to simplify logic in components */

/*
Complain!

TODO we have too many ways to subscribe to / dispatch events in our app...

- `IPlatformEvent` that come down directly from the server via subscriptions like `registerForEventAndSubscribe` in this file
  - some other way of handling these in web.core, but I do not remember what they are
-  ClientEvents - that whole construct
  - keeping track of subscribers
    - we have a good / simple / code-tested mechanism for keeping track of subscribers in this file...
  - dispatching and handling and re-routing of various events
-  PubSub 
  - at it's core just a simple library for keeping track of subscriptions and un-opinionated way of notifying subscribers
    - we have a good / simple / code-tested mechanism for keeping track of subscribers in this file...
  - a number of event names overlap between PubSub and ClientEvents... this is confusing
-  EventsHub - overlaps / empowers PubSub
  - ended up duplicating some functions from here in EventHub methods
-  just window.dispatchEvent / window.addEventListener - which for a number of things is fine, but it's low level from a subscription management point of view.

Why do we need so much complexity and variety? 
  - probably:
    - different people doing different things at different times, unaware of what others had done?
    - adding new and improved things without replacing uses of the old things
Could we / should we consider consolidating some of this?
  - Discussed on Holistic Meeting, and suggested replace PubSub, or make it a shell that points to this.
    - would involve flipping the order of some JS bundle loading, 
    - could we eliminate some of the bootstrap timing problem between ClientEvents and EventsHub at the same time?

*/

// const KEEP_ALIVE_INTERVAL = 120000; // 2 min poll.

// Using maps for convenience over array manipulation logic here,
// but a nested map, so it's easy for components to use the same handler
// for multiple IDs with no extra work, e.g. fetch data based a timer, but also on folder change.
// forcing the nested ID here is annoying for some cases, but at least forces the developer to be intentional.
const listeners = new Map<string, Map<string, (event: any) => void>>();
const clientListeners = new Map<string, Map<string, Function>>();
const subscriberIds = new Map<string, number>();
let isSocketConnectionReady = false;
export const ACTIONS_EVENT_ID = 'ActionsEventId';
/**
 * Simply notify the server that this client needs to know about this event.
 * @param eventId event id to tell the server about
 */
export async function registerForEvent(eventId: string) {
  if (!isSocketConnectionReady) {
    // We won't register right away, we'll save it. Whenever EventHub connection is ready, it will register all events
    // which are already registered here or via PubSub.
    // It's also unlikely that the ServiceFetcher.getUserContext().ClientEventSessionId is ready at this point,
    // we might need to revisit how that is bootstrapped. Why shouldn't clientEventSessionId exist before CoreScripts partial runs?
    return;
  }

  //const clientEventsSessionID = ServiceFetcher.getUserContext().ClientEventSessionId;
  try {
    await eventsHub.registerEvent(eventId);
  } catch (error) {
    console.error('Error registering for event', error);
  }
}

/**
 * Simply notify the server that this client no longer needs to know about this event.
 * @param eventId event id to tell the server about
 */
export async function unRegisterForEvent(eventId: string) {
  try {
    await eventsHub.unRegisterEvent(eventId);
  } catch (error) {
    console.error('Error un-registering for event', error);
  }
}

/**
 * Register, and listen for particular events.
 *
 * Forcing a subscriber ID is annoying for some use-cases, but makes sure this isn't leaky,
 * developers are intentional can use useCallback safely.
 *
 *  If you don't have a meaningful ID handy, try
 *
 * ```tsx
 * // helper outside components:
 * const id = getUniqueComponentId('component-name');
 *
 * // or hook inside components:
 * const id = useUniqueSubscriberId('component-name');
 *
 * // remember to unsubscribe, or use `useClientEventServiceSubscription` hook to do the same:
 * useEffect(() => {
 *    const componentId = id.current;
 *    subScribeToEvent(eventId, componentId, listener);
 *    return () => {
 *      unSubScribeToEvent(eventId, componentId, listener);
 *    };
 * }, [listener]);
 * ```
 *
 * @param eventId to listen for events for this id
 * @param subscriberId - componentId name, etc. to allow several listeners for the same event Id.
 * `getUniqueComponentId`, or `useUniqueSubscriberId` hook can help.
 * @param listener to handle the events. without this, it simply registers the event with the ClientEventsService.
 * which may be replaced if something else subscribes in exactly the same way
 */
export async function registerForEventAndSubscribe(
  eventId: string,
  subscriberId: string,
  listener: (event: any) => void
) {
  if (!eventHasSubscribers(eventId)) {
    // If we already have subscribers, we should already be registered for this event.
    // `EventsHub.afterConnectionSuccess` timer, and `pingWithFolders` should keep them up to date with the server.
    await registerForEvent(eventId);
  }
  subScribeToEvent(eventId, subscriberId, listener);
}

/**
 * Unregister (service) and unsubscribe (client) from particular event ID.
 * @param eventId to listen for
 * @param subscriberId subscribing component, reason, etc.
 * @param listener to remove. If not provided, will be looked up via ids.
 */
export async function unRegisterForEventAndUnsubscribe(
  eventId: string,
  subscriberId: string,
  listener?: (event: any) => void
) {
  // remove client side subscription optimistically
  const wasLastSubscriber = unSubScribeToEvent(eventId, subscriberId, listener);
  // remove listener if this was the last thing listening.
  if (wasLastSubscriber && !getPubSubMessageNames().includes(eventId)) {
    await unRegisterForEvent(eventId);
  }
}

function getPubSubMessageNames() {
  // safely, in case of storybook, etc. where PubSub is not defined.
  return getPubSub().getMessageNames();
}

/**
 * Set up client event subscription.
 *
 * _Use this directly only if you know event is going to be registered with server by another mechanism._
 *
 * Forcing a subscriber ID is annoying for some use-cases, but makes sure this isn't leaky,
 * developers are intentional can use useCallback safely.
 *
 *  If you don't have a meaningful ID handy, try
 *
 * ```tsx
 * const id = useRef(generateGuid());
 *
 * useEffect(() => {
 *    const componentId = id.current;
 *    subScribeToEvent(eventId, componentId, listener);
 *    return () => {
 *      unSubScribeToEvent(eventId, componentId, listener);
 *    };
 * }, [listener]);
 * ```
 *
 * @param eventId to listen for
 * @param subscriberId subscribing component, reason, etc.
 * `getUniqueComponentId`, or `useUniqueSubscriberId` hook can help.
 * @param listener to remove
 */
export function subScribeToEvent(eventId: string, subscriberId: string, listener: (event: any) => void) {
  // remove anything for this ID first, to avoid leaking handlers
  removeStoredListenerFromWindow(listeners, eventId, subscriberId);
  // add event subscriptions
  const forId = listeners.get(eventId) ?? new Map<string, (event: any) => void>();
  forId.set(subscriberId, listener);
  listeners.set(eventId, forId);
  window.addEventListener(eventId, listener);
}

/**
 * Remove client event subscription.
 *
 * Use this directly only if you know event is going to be un-registered by another mechanism.
 *
 * @param eventId being listened for
 * @param subscriberId subscribing component, reason, etc.
 * @param listener to remove. If not provided, will be looked up via ids.
 * @param returns true if something is still subscribed to that event.
 */
export function unSubScribeToEvent(eventId: string, subscriberId: string, listener?: (event: any) => void) {
  if (listener) {
    window.removeEventListener(eventId, listener);
  }

  const forId = listeners.get(eventId);
  if (!forId) {
    return;
  }

  removeStoredListenerFromWindow(listeners, eventId, subscriberId);

  // remove listener using handler as key
  forId.delete(subscriberId);

  // remove the root entry map to prevent memory leak and keep base list of subscribed IDs correct.
  if (!forId.size) {
    listeners.delete(eventId);
    return true;
  }

  return false;
}

/**
 * Check for and remove any stored listeners, in case someone messed up `useCallback` etc.
 * and avoid leaking them onto window
 * @param stored map of stored listeners
 * @param eventId it was registered with
 * @param subscriberId component, etc. listening for this handler
 */
function removeStoredListenerFromWindow(stored: typeof listeners, eventId: string, subscriberId: string) {
  const forId = stored.get(eventId);
  if (!forId) {
    return;
  }

  const storedListener = forId.get(subscriberId);

  storedListener && window.removeEventListener(eventId, storedListener);
}

//#region client event listeners

/**
 * Subscribe to client event
 * @param eventId to subscribe to
 * @param componentId subscribing to the event
 * @param listener handler
 */
export function addClientEventListener(eventId: string, componentId: string, listener: (event: any) => void) {
  const forId = clientListeners.get(eventId) ?? new Map<string, (event: any) => void>();
  forId.set(componentId, listener);
  clientListeners.set(eventId, forId);
}

/**
 * Unsubscribe from client event id
 * @param eventId to subscribe to
 * @param componentId subscribing
 */
export function removeClientEventListener(eventId: string, componentId: string) {
  const forId = clientListeners.get(eventId);
  if (!forId) {
    return;
  }

  // remove listener using handler as key
  forId.delete(componentId);

  // remove the root entry map to prevent memory leak and keep base list of subscribed IDs correct.
  if (!forId.size) {
    clientListeners.delete(eventId);
  }
}

//#endregion

/** This is used in legacy PubSub logic when checking listener IDs to emit values to. */
export function getRegisteredListenerIds() {
  // use Set to get distinct ids:
  return Array.from(new Set(listeners.keys()));
}

export function eventHasSubscribers(eventName: string) {
  return listeners.has(eventName);
}

export function setConnectionReady() {
  isSocketConnectionReady = true;
}

/**
 * Get listeners to call for an event id/name
 * @param eventName to get handlers for
 */
export function getClientListeners(eventName: string) {
  const byName = clientListeners.get(eventName);
  return byName ? Array.from(byName.values()) : [];
}

/** This is intended only for code-tests */
export function getRegisteredClientEventListenersForUnitTests() {
  return listeners;
}

export function getUniqueComponentId(componentName: string) {
  const forName = subscriberIds.get(componentName);
  // This incremental id is as sophisticated as PubSub uses across all names, and we've been using that for years.
  // Should be faster than generating a GUID.
  // It's also spread across component names, so hitting Number.Max is very unlikely.
  const id = forName ? forName + 1 : 1;
  subscriberIds.set(componentName, id);
  return `${componentName}_${id}`;
}

function getMessageNamesWithPubSub() {
  // Why does this only feed stuff from PubSub, and not this utility?
  return getPubSub().GetRegisterMessegeeNames();
}

/**
 * Notify's server of update list of events UI instance / browser tab cares about.
 * (Helper to keep `clientEventsSessionId` concerns in one place.)
 * @param folderIds which the UI instance / browser tab cares about, right now.
 */
export async function syncClientsEventAndFoldersListToServer(currentFolderId?: string) {
  // pingWithFolders is not a descriptive name, but it seems to keep ClientEventsService aware of which event names and
  // folderIds a particular UI session cares about.
  pingWithFolders({
    // does ServiceFetcher not add clientEventsSessionId automatically?
    clientEventsSessionId: ServiceFetcher.getUserContext().ClientEventSessionId,
    clientEvents: getMessageNamesWithPubSub(),
    folderIds: getVisibleFolders(currentFolderId),
  });
}

// expose for external use
export const ClientEventUtils = {
  getClientListeners,
  getRegisteredListenerIds,
  registerForEvent: registerForEventAndSubscribe,
  subScribeToEvent,
  unRegisterForEvent: unRegisterForEventAndUnsubscribe,
  unSubScribeToEvent,
  flatEvents,
};

export const ClientMessage = {
  FolderChanged: 'FolderChangedMessage',
  FolderEntitiesChanged: 'FolderEntitiesChangedMessage',
};

/**
 * Wrapper around legacy event disseminator, for now.
 * @param eventName name of event
 * @param eventData data to distribute
 */
export function sendClientEventNotification(eventName: string, eventData: any) {
  if (IS_TEST || DEV_SERVER || process.env.STORYBOOK) {
    // do something else?
    console.log('firing ClientEvent', eventData);
    return;
  }

  Decisions.ClientEventsHandler.SendNotificationToClient(eventName, eventData);
}

/**
 * Make events flat. Sometimes it is necessary as we may use SetEvents to send events as single event to the client side.
 * So it unpacks SetEvents event.
 *
 * Inputs:
 * [RuleNotificationEvent, SetEvents {Events: [RuleNotificationEvent, RuleNotificationEvents]}]
 *
 * Result:
 * [[RuleNotificationEvent, RuleNotificationEvent, RuleNotificationEvents]
 */
export function flatEvents(_events: any[] | any): any[] {
  const events = Array.isArray(_events) ? _events : [_events];

  let flatEventsResult = [];

  for (const notification of events) {
    if (isSetEvents(notification)) {
      const events = (notification as SetEvents).Events.map(item => item.OutputValue);
      flatEventsResult.push(...flatEvents(events));
    } else {
      flatEventsResult.push(notification);
    }
  }

  return flatEventsResult;
}
